import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * Application-level modal built around the a2k window component.
 *
 * Keeping confirmation behavior in one component makes destructive flows
 * consistent and prevents resource views from falling back to browser dialogs.
 */
@customElement("app-dialog")
export class AppDialog extends LitElement {
  @property({ type: Boolean }) open = false;
  @property() heading = "";
  @property({ attribute: "confirm-label" }) confirmLabel = "";
  @property({ attribute: "cancel-label" }) cancelLabel = "";
  @property({ attribute: "working-label" }) workingLabel = "";
  @property({ type: Boolean }) busy = false;
  @property({ type: Boolean, attribute: "hide-confirm" }) hideConfirm = false;
  @property() message = "";

  static styles = css`
    :host {
      display: contents;
    }

    .backdrop {
      position: fixed;
      inset: 0;
      z-index: 1500;
      display: grid;
      place-items: center;
      padding: 20px;
      background: rgb(18 32 53 / 45%);
    }

    cpl-a2k-window {
      display: block;
      width: min(92vw, 960px);
      max-width: 960px;
      min-width: 0;
      --window-width: min(92vw, 960px);
      box-sizing: border-box;
      overflow: hidden;
    }

    .body {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: 14px;
      min-height: 0;
      min-width: 0;
      max-width: 100%;
      box-sizing: border-box;
      overflow-x: hidden;
      overflow-y: visible;
      overflow-wrap: anywhere;
      word-break: break-word;
      padding-bottom: 16px;
    }

    .message {
      margin: 0;
      padding: 9px;
      color: #8b1717;
      background: #f5dddd;
      border: 1px solid #8b1717;
    }

    .actions {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 8px;
      box-sizing: border-box;
      min-width: 0;
      max-width: 100%;
      padding: 8px var(--window-spacing-horizontal, 16px);
      background: var(--panel-color-background);
    }

    @media (max-width: 650px) {
      .backdrop {
        padding: 12px;
      }

      cpl-a2k-window {
        width: calc(100vw - 24px);
        --window-width: calc(100vw - 24px);
      }

      .body {
        padding: 12px;
      }

      .actions {
        align-items: stretch;
        flex-direction: column-reverse;
      }

      .actions > * {
        width: 100%;
      }
    }
  `;

  disconnectedCallback() {
    this.removeEventListener("close", this.handleWindowClose, true);
    super.disconnectedCallback();
  }

  private readonly handleWindowClose = (event: Event) => {
    event.stopPropagation();
    this.cancel();
  };

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("close", this.handleWindowClose, true);
  }

  private cancel() {
    if (!this.busy) {
      this.dispatchEvent(new CustomEvent("dialog-cancel"));
    }
  }

  private approve() {
    if (!this.busy) {
      this.dispatchEvent(new CustomEvent("dialog-confirm"));
    }
  }

  render() {
    if (!this.open) return null;
    return html`<div
      class="backdrop"
      role="presentation"
      @keydown=${(event: KeyboardEvent) => {
        if (event.key === "Escape") this.cancel();
      }}
    >
      <cpl-a2k-window
        heading=${this.heading}
        role="dialog"
        aria-modal="true"
        aria-label=${this.heading}
        layout="flow"
        ?draggable=${false}
        ?closeable=${true}
      >
        <div class="body">
          <slot></slot>
          ${this.message ? html`<p class="message">${this.message}</p>` : null}
        </div>
        <div class="actions" slot="statusbar">
          <a2k-button ?disabled=${this.busy} @click=${this.cancel}>
            ${this.cancelLabel}
          </a2k-button>
          ${
            this.hideConfirm
              ? null
              : html`<a2k-button ?disabled=${this.busy} @click=${this.approve}>
                  ${this.busy ? this.workingLabel : this.confirmLabel}
                </a2k-button>`
          }
        </div>
      </cpl-a2k-window>
    </div>`;
  }
}
